import { Stack } from "@mui/material";
import { RootPaths } from "@src/appV2/App/paths";
import { useQueryClient } from "@tanstack/react-query";
import pluralize from "pluralize";
import { useEffect, useMemo, useRef } from "react";

import { StickyScrollPageHeader } from "../../components/StickyScrollPageHeader";
import { invalidateShifts } from "../../invalidateShifts";
import { useShiftBundles } from "../../Shift/Bundle/useShiftBundles";
import { OpenShiftListDataProvider } from "../../Shift/Open/useOpenShiftListDataContext";
import { VirtualShiftListContextProvider } from "../../Shift/Open/useVirtualShiftListContext/useVirtualShiftListContext";
import { useScrollToTopOnStatusTap } from "../../useScrollToTopOnStatusTap";
import { PullToRefresh } from "../ListView/PullToRefresh";
import { SHIFT_DISCOVERY_LIST_PATH, SHIFT_DISCOVERY_PRIORITY_PATH } from "../paths";
import { useShiftModalsDataContext } from "../useShiftModalsDataContext";
import { PriorityShiftsList } from "./List";
import { usePriorityShiftsData } from "./usePriorityShiftsData";

export function ShiftDiscoveryPriorityShiftsPage() {
  const queryClient = useQueryClient();
  const stackRef = useRef<HTMLDivElement>(null);

  useScrollToTopOnStatusTap(stackRef);

  const { setBaseNavigationPathOverride } = useShiftModalsDataContext();

  /**
   * When this page is opened, we want to override the base navigation path
   * so that when shift modals are opened from here, they are rendered on top of this page
   */
  useEffect(() => {
    setBaseNavigationPathOverride(SHIFT_DISCOVERY_PRIORITY_PATH);
    return () => {
      setBaseNavigationPathOverride(undefined);
    };
  }, [setBaseNavigationPathOverride]);

  useEffect(() => {
    // invalidate open shifts so when user gets back to list view,
    // they don't see discrepancies between priority shifts count here
    // and in the list view callout
    void invalidateShifts(queryClient);
  }, [queryClient]);

  const {
    shifts: priorityShifts,
    allShifts,
    isError,
    isLoading,
    isSuccess,
    workplacesMap,
    rateNegotiationsMap,
    shiftAuctionsMap,
    pagination,
  } = usePriorityShiftsData();

  const eligibleWorkplaceIds = useMemo(() => {
    const eligibleIds = new Set<string>();
    if (!workplacesMap) {
      return eligibleIds;
    }

    for (const [workplaceId, workplace] of workplacesMap) {
      if (workplace.attributes.isShiftBundlesEnabled) {
        eligibleIds.add(workplaceId);
      }
    }

    return eligibleIds;
  }, [workplacesMap]);

  const bundlesByShiftId = useShiftBundles(allShifts, {
    eligibleWorkplaceIds,
  });

  return (
    <Stack
      sx={(theme) => ({
        flex: 1,
        backgroundColor: theme.palette.background.tertiary,
        overflow: "hidden",
        position: "relative",
      })}
    >
      <OpenShiftListDataProvider
        openShifts={priorityShifts}
        workplacesMap={workplacesMap}
        rateNegotiationsMap={rateNegotiationsMap}
        shiftAuctionsMap={shiftAuctionsMap}
        bundlesByShiftId={bundlesByShiftId}
        isOpenShiftsLoading={isLoading}
        isOpenShiftsError={isError}
        isOpenShiftsSuccess={isSuccess}
        canLoadMoreShifts={pagination?.hasNextPage ?? false}
        isLoadingMoreShifts={pagination?.isFetchingNextPage ?? false}
        loadMoreShifts={() => {
          void pagination?.fetchNextPage();
        }}
      >
        <VirtualShiftListContextProvider shifts={priorityShifts} workplacesMap={workplacesMap}>
          <PullToRefresh
            scrollRef={stackRef}
            onRefresh={async () => {
              await invalidateShifts(queryClient);
            }}
          />

          <StickyScrollPageHeader
            title={`${priorityShifts.length > 0 ? priorityShifts.length : ""} Priority ${pluralize(
              "Shift",
              priorityShifts.length
            )}`}
            headerVariant="tertiary"
            scrollContainerRef={stackRef}
            fallbackGoBackPath={`${RootPaths.APP_V2_HOME}/${SHIFT_DISCOVERY_LIST_PATH}`}
          />

          <Stack
            ref={stackRef}
            data-testid="priority-shifts-page-scroll-container"
            spacing={7}
            sx={{
              flex: 1,
              overflow: "auto",
              paddingX: 5,
              paddingBottom: 6,
            }}
          >
            <PriorityShiftsList scrollRef={stackRef} />
          </Stack>
        </VirtualShiftListContextProvider>
      </OpenShiftListDataProvider>
    </Stack>
  );
}
